'use client';
import { Controller, useForm } from 'react-hook-form';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Stack } from '@startuptools/ui/Stack';
import { Input } from '@startuptools/ui/Input';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import { IconButton } from '@startuptools/ui/IconButton';
import { SelectIdentity } from '@startuptools/business-ui/identity/select/SelectIdentity';
import { Box } from '@startuptools/ui/Box';
import React from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import Checkbox from '@mui/joy/Checkbox';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import {
  templateFields,
  FieldType,
  fieldSchema,
  booleanFieldSchema,
  dateFieldSchema,
  variableTextFieldSchema,
  identityFieldSchema,
  createIdentitySchema,
  Field,
  multipleChoiceFieldSchema,
} from './types';
import { Divider } from '@startuptools/ui/Divider';
import { Textarea } from '@startuptools/ui/Textarea';
import { InputState } from '@startuptools/business-ui/identity/types';
import { z } from 'zod';
import ArrayInput from './TextArrayInput';

export const FieldForm = ({ onSubmit }: { onSubmit: (data: Field) => void }) => {
  const form = useForm<Field>({
    defaultValues: {
      type: undefined,
      name: '',
      valueOptions: [''],
    },
  });
  return (
    <form
      onSubmit={form.handleSubmit(data => {
        onSubmit(data);
        form.reset();
      })}
    >
      <Stack gap={2}>
        <Controller
          name="type"
          control={form.control}
          rules={{ required: true }}
          render={({ field: { value, onChange, ...props } }) => (
            <FormControl error={!!form.formState.errors.type}>
              <FormLabel>Type</FormLabel>
              <Select
                {...props}
                value={value ?? null}
                onChange={(_e, newValue) => onChange(newValue)}
                placeholder="Select a type"
              >
                {templateFields.map(v => (
                  <Option key={v.type} value={v.type}>
                    {v.label}
                  </Option>
                ))}
              </Select>
              {form.formState.errors.type && <FormHelperText>Type is required</FormHelperText>}
            </FormControl>
          )}
        />

        <FormControl error={!!form.formState.errors.name}>
          <FormLabel>Name</FormLabel>
          <Input {...form.register('name', { required: true })} />
          {form.formState.errors.name && <FormHelperText>Name is required</FormHelperText>}
        </FormControl>

        {form.watch('type') === FieldType.VARIABLE_TEXT && (
          <FormControl error={'longForm' in form.formState.errors && !!form.formState.errors.longForm}>
            <Checkbox {...form.register('longForm')} label="Long form" />
            <FormHelperText>Let's user input text in a bigger text area</FormHelperText>
          </FormControl>
        )}

        {form.watch('type') === FieldType.MULTIPLE_CHOICE && (
          <ArrayInput control={form.control} register={form.register} name="options" label="Options" />
        )}

        <Box>
          <Button type="submit">Create</Button>
        </Box>
      </Stack>
    </form>
  );
};

const DisplayOrEditText = ({ value, onChange }: { value: string | undefined; onChange: (value: string) => void }) => {
  const [editing, setEditing] = React.useState(false);
  const ref = React.useRef<HTMLInputElement>(null);

  const save = () => {
    if (ref.current) {
      onChange(ref.current.value);
    }
    setEditing(false);
  };

  return editing ? (
    <Input
      defaultValue={value}
      onBlur={save}
      slotProps={{ input: { ref } }}
      endDecorator={
        <IconButton onClick={save}>
          <SaveIcon />
        </IconButton>
      }
    />
  ) : (
    <Stack direction="row" gap={1}>
      <Typography>{value || 'Not set'}</Typography>
      <Box>
        <IconButton onClick={() => setEditing(true)}>
          <EditIcon />
        </IconButton>
      </Box>
    </Stack>
  );
};

const DisplayOrEditTextLong = ({
  defaultValue,
  onChange,
}: {
  defaultValue: string | undefined;
  onChange: (value: string) => void;
}) => {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState<string>(defaultValue ?? '');

  const save = () => {
    onChange(value);
    setEditing(false);
  };

  return editing ? (
    <Textarea
      defaultValue={value}
      onBlur={save}
      onChange={e => setValue(e.target.value)}
      endDecorator={
        <IconButton onClick={save}>
          <SaveIcon />
        </IconButton>
      }
    />
  ) : (
    <Stack direction="row" gap={1}>
      <Typography>{value || 'Not set'}</Typography>
      <Box>
        <IconButton onClick={() => setEditing(true)}>
          <EditIcon />
        </IconButton>
      </Box>
    </Stack>
  );
};

const EditBoolean = ({
  field,
  onChange,
}: {
  field: z.infer<typeof booleanFieldSchema>;
  onChange: (value: z.infer<typeof booleanFieldSchema>['value']['value']) => void;
}) => {
  return (
    <Checkbox label={field.name} checked={field.value?.value ?? false} onChange={e => onChange(e.target.checked)} />
  );
};

const EditDate = ({
  field,
  onChange,
}: {
  field: z.infer<typeof dateFieldSchema>;
  onChange: (value: z.infer<typeof dateFieldSchema>['value']['value']) => void;
}) => {
  return (
    <DatePicker
      slotProps={{
        field: { clearable: true, onClear: () => onChange(null) },
        textField: { size: 'small' },
      }}
      value={field.value?.value ? DateTime.fromJSDate(field.value?.value) : null}
      onChange={newValue => {
        onChange(newValue ? newValue.toJSDate() : null);
      }}
    />
  );
};

const EditMultipleChoice = ({
  field,
  onChange,
}: {
  field: z.infer<typeof multipleChoiceFieldSchema>;
  onChange: (value: z.infer<typeof multipleChoiceFieldSchema>['value']['value']) => void;
}) => {
  return (
    <Select
      value={field.value?.value}
      onChange={(_event, value) => onChange(value as string)}
      placeholder="Select an option"
    >
      {field.valueOptions?.map(option => (
        <Option key={option} value={option}>
          {option}
        </Option>
      ))}
    </Select>
  );
};

const EditText = ({
  field,
  onChange,
}: {
  field: z.infer<typeof variableTextFieldSchema>;
  onChange: (value: z.infer<typeof variableTextFieldSchema>['value']['value']) => void;
}) => {
  return field.longForm
    ? DisplayOrEditTextLong({ defaultValue: field.value?.content ?? '', onChange })
    : DisplayOrEditText({ value: field.value?.value ?? '', onChange });
};

const EditIdentity = ({
  field,
  onChange,
  previewMode,
}: {
  field: z.infer<typeof identityFieldSchema>;
  onChange: (value: z.infer<typeof createIdentitySchema>) => void;
  previewMode: boolean;
}) => {
  return (
    <SelectIdentity
      availableInputStates={{
        [InputState.System]: !previewMode,
        [InputState.Swedish]: !previewMode,
        [InputState.Foreign]: !previewMode,
        [InputState.Fictitious]: previewMode,
      }}
      multiple={false}
      value={field.value?.value}
      identity={field.value?.content}
      createButton={props => (
        <IconButton sx={{ flexShrink: 0 }} {...props}>
          <EditIcon />
        </IconButton>
      )}
      handleCreate={async identity => {
        return onChange(identity);
      }}
    />
  );
};

export const SetField = ({
  field,
  onChange,
  previewMode,
}: {
  field: z.infer<typeof fieldSchema> | undefined;
  onChange: (value: { value: unknown }) => void;
  previewMode: boolean;
}) => {
  switch (field?.type) {
    case FieldType.BOOLEAN:
      return <EditBoolean field={field} onChange={newValue => onChange({ value: newValue })} />;
    case FieldType.DATE:
      return <EditDate field={field} onChange={newValue => onChange({ value: newValue })} />;
    case FieldType.VARIABLE_TEXT:
      return <EditText field={field} onChange={newValue => onChange({ value: newValue })} />;
    case FieldType.IDENTITY:
      return (
        <EditIdentity field={field} onChange={newValue => onChange({ value: newValue })} previewMode={previewMode} />
      );
    case FieldType.MULTIPLE_CHOICE:
      return <EditMultipleChoice field={field} onChange={newValue => onChange({ value: newValue })} />;
    default:
      return <Typography level="body-sm">Could not find field.</Typography>;
  }
};

export const InsertField = ({
  createField,
  insertField,
  fields,
}: {
  createField: (data: Field) => Promise<void>;
  insertField: (data: Field) => void;
  fields: Field[];
}) => {
  const form = useForm<{ fieldId: string }>();
  const shownFields = fields.filter(field => field.type !== FieldType.BOOLEAN);
  return (
    <Stack gap={2}>
      {Boolean(shownFields.length) && (
        <>
          <Typography>Choose existing field</Typography>
          <form onSubmit={form.handleSubmit(data => insertField(fields.find(field => field.id === data.fieldId)!))}>
            <Stack gap={1}>
              <Controller
                control={form.control}
                name="fieldId"
                render={({ field: { onChange, ...field } }) => (
                  <FormControl>
                    <Select
                      value={field.value ?? ''}
                      onChange={(_, value) => onChange(value)}
                      placeholder="Select field..."
                    >
                      {shownFields.map(field => (
                        <Option key={field.id} value={field.id}>
                          {field.name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Box>
                <Button type="submit">Insert</Button>
              </Box>
            </Stack>
          </form>
          <Divider>Or create new</Divider>
        </>
      )}

      <FieldForm onSubmit={createField} />
    </Stack>
  );
};

export const EditField = ({
  field,
  onDelete,
  onSave,
}: {
  field: Field;
  onDelete: () => void;
  onSave: (field: Field) => Promise<void>;
}) => {
  const form = useForm<Field>({
    defaultValues: field,
  });
  return (
    <Stack>
      <Typography>{field.name}</Typography>
      <Button variant="outlined" color="danger" onClick={onDelete}>
        Delete
      </Button>
      {field.type === FieldType.MULTIPLE_CHOICE && (
        <form onSubmit={form.handleSubmit(onSave)}>
          <Stack gap={1}>
            <ArrayInput control={form.control} register={form.register} name="valueOptions" label="Options" />
            <Button type="submit">Save</Button>
          </Stack>
        </form>
      )}
    </Stack>
  );
};
